import _ from "lodash";
import { SID_LOOKUP } from "../../app.constants";
import { sortNatural } from "../../common/common/common.utils";
import { SIGNER } from "../../common/paperwork/constants";
import { getNextRole } from "../../common/paperwork/paperwork.utils";
import {
    buildRoleScopedFormSchema,
    filterOutBadEnums,
    getDocumentFieldPathData,
    getSignatureFieldNames,
    getReactSchemaFormData,
} from "../../common/paperwork/document_field_path.utils";

export const PAPERWORK_READY_DATA_SOURCE_REQUEST_DATA = "request_data";
export const PAPERWORK_READY_DATA_SOURCE_PREFILL_ATTEMPT = "prefill_attempt";

function hasValueForRoleData(value) {
    if (_.isNil(value)) {
        return false;
    }
    if (_.isString(value)) {
        return _.trim(value) !== "";
    }
    if (_.isArray(value)) {
        return value.length > 0;
    }
    if (_.isPlainObject(value)) {
        return !_.isEmpty(value);
    }
    return true;
}

export function hasRoleDataInRequest(
    documentPackageRequest,
    documentPackage,
    role,
) {
    let roleFieldNames = _.get(documentPackage, ["fields", role], []);
    return _.some(roleFieldNames, function (fieldName) {
        return hasValueForRoleData(
            _.get(documentPackageRequest, ["data", fieldName]),
        );
    });
}

export function getNonSignatureRoleFieldNames(
    documentPackage,
    role,
    documentPackageTranslated,
) {
    let roleFieldNames = _.get(documentPackage, ["fields", role], []);
    let signatureFieldNames = _.isPlainObject(
        _.get(documentPackageTranslated, "schema", null),
    )
        ? getSignatureFieldNames(documentPackageTranslated)
        : [];
    return _.difference(roleFieldNames, signatureFieldNames);
}

export function getValidationDataForPaperwork(
    documentPackageRequest,
    documentPackage,
    documentPackageTranslated,
    role,
    resolvedPrefillAttempt,
) {
    let hasRoleData = hasRoleDataInRequest(
        documentPackageRequest,
        documentPackage,
        role,
    );
    let source = PAPERWORK_READY_DATA_SOURCE_PREFILL_ATTEMPT;
    let sourceData = _.get(resolvedPrefillAttempt, "suggested_data", {});
    let hasResolvedPrefillAttempt = !!resolvedPrefillAttempt;
    if (hasRoleData) {
        source = PAPERWORK_READY_DATA_SOURCE_REQUEST_DATA;
        sourceData = _.get(documentPackageRequest, "data", {});
        hasResolvedPrefillAttempt = false;
    }

    let preparedData = sourceData;
    if (documentPackage && documentPackageTranslated && role) {
        let formSchema = buildRoleScopedFormSchema(
            documentPackageTranslated,
            documentPackage,
            documentPackageRequest,
            role,
        );
        let filteredPrefillData = filterOutBadEnums(
            _.cloneDeep(_.get(resolvedPrefillAttempt, "suggested_data", {})),
            formSchema,
        );
        let prefillDataForRole = getReactSchemaFormData(
            filteredPrefillData,
            formSchema,
        );
        let requestDataForRole = getReactSchemaFormData(
            _.get(documentPackageRequest, "data", {}),
            formSchema,
        );
        let mergedFormData = getReactSchemaFormData(
            _.merge({}, prefillDataForRole, requestDataForRole),
            formSchema,
        );
        let defaultedRoleFormData = getReactSchemaFormData(
            documentPackage.makeDefaultDates(mergedFormData, role, formSchema),
            formSchema,
        );
        preparedData = getDocumentFieldPathData(
            defaultedRoleFormData,
            formSchema,
        );
    }

    if (hasRoleData) {
        return {
            source,
            data: preparedData,
            resolvedPrefillAttempt: null,
            hasResolvedPrefillAttempt,
        };
    }

    return {
        source,
        data: preparedData,
        resolvedPrefillAttempt: resolvedPrefillAttempt || null,
        hasResolvedPrefillAttempt,
    };
}

export function getLatestPrefillAttemptByRequestSid(prefillAttempts) {
    let prefillAttemptByRequestSid = {};
    _.forEach(
        _.orderBy(prefillAttempts, ["created"], ["desc"]),
        function (prefillAttempt) {
            let requestSid = prefillAttempt.document_package_request;
            if (!prefillAttemptByRequestSid[requestSid]) {
                prefillAttemptByRequestSid[requestSid] = prefillAttempt;
            }
        },
    );
    return prefillAttemptByRequestSid;
}

export function shouldNotifySigner(requests, documentPackages) {
    for (let request of requests) {
        let documentPackage = _.find(documentPackages, [
            SID_LOOKUP,
            request.document_package,
        ]);
        let nextRole = getNextRole(request, documentPackage);
        if (nextRole === SIGNER && !request.initiator_fill_first) {
            return true;
        }
    }
    return false;
}

export function getDocumentDataForSelect(
    documents,
    administrators,
    customizations,
    customizationTranslateds,
    documentPackageTranslateds,
) {
    let documentData = _.flatten(
        _.map(documents, function (document) {
            let documentPackageTranslated =
                document.getDocumentPackageTranslated(
                    documentPackageTranslateds,
                );
            let thisDocumentsCustomizations = _.filter(customizations, [
                "document_package",
                document.sid,
            ]);
            if (thisDocumentsCustomizations.length === 0) {
                return [
                    {
                        document,
                        documentPackageTranslated,
                        administrator: _.find(administrators, [
                            SID_LOOKUP,
                            document.administrator,
                        ]),
                        displayName: documentPackageTranslated.name,
                    },
                ];
            } else {
                return _.compact(
                    _.map(
                        thisDocumentsCustomizations,
                        function (customization) {
                            let thisCustomizationsTranslateds = _.filter(
                                customizationTranslateds,
                                [
                                    "document_package_customization",
                                    customization.sid,
                                ],
                            );
                            return {
                                document,
                                documentPackageTranslated,
                                administrator: _.find(administrators, [
                                    SID_LOOKUP,
                                    document.administrator,
                                ]),
                                customization,
                                displayName:
                                    documentPackageTranslated.name +
                                    " (" +
                                    customization.name +
                                    ")",
                                customizationIsValid:
                                    document.customizationIsValid(
                                        thisCustomizationsTranslateds,
                                        documentPackageTranslated,
                                    ),
                            };
                        },
                    ),
                );
            }
        }),
    );
    return documentData;
}

export function addMetaDataToDocumentPackageOptions(opts, $translate) {
    let documentData = _.map(opts, function (data) {
        data.administratorName = _.get(data, "administrator.name", null);
        data.customizationName = _.get(data, "customization.name", null);
        data.disabled =
            data.document.requiresCustomization(
                data.documentPackageTranslated,
            ) &&
            (!data.customization || !data.customizationIsValid);

        if (data.customization && data.customizationIsValid) {
            data.customizationLabel = data.customization.name;
            data.customizationLabelClass = "text-gray-500";
        } else if (
            data.document.requiresCustomization(data.documentPackageTranslated)
        ) {
            data.customizationLabel = $translate.instant(
                "contract.memberRequestPaperwork.CUSTOMIZATION_REQUIRED_LABEL",
            );
            data.customizationLabelClass = "text-red-500";
        } else {
            data.customizationLabel = null;
            data.customizationLabelClass = null;
        }

        data.searchText = _.compact([
            data.displayName,
            data.administratorName,
            data.customizationName,
            data.customizationLabel,
        ]).join(" ");

        return data;
    });

    let sortedByAdministratorName = sortNatural(
        documentData,
        "administratorName",
    );
    return sortNatural(sortedByAdministratorName, "displayName");
}
